import { Component, OnInit, OnDestroy } from '@angular/core';
import { DirectoryModel } from '@classes/wiki/directory-model.model';
import { FormGroup, FormBuilder } from '@angular/forms';
import { EditorService } from '@services/wiki/editor.service';
import { FileService } from '@services/wiki/file.service';
import { Router } from '@angular/router';
import { FileOrDirService } from '@services/wiki/file-or-dir.service';
import { MatDialog } from '@angular/material/dialog';
import { UtilitiyService } from '@services/wiki/utility.service';
import { UiService } from '@services/ui.service';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlattener, MatTreeFlatDataSource } from '@angular/material/tree';
import { CreateDirOrFile } from '@classes/wiki/create-dir-or-file.model';
import { FileToDelete } from '@classes/wiki/file-to-delete.model';
import { ConfirmDialogComponent } from 'app/shared/dialogs/confirm-dialog/confirm-dialog.component';
import { Subscription } from 'rxjs';
import { AuthService } from '@services/auth.service';
import { LicenceRoleType } from '@enums/licence-role-type.enum';

interface FlatNode {
  name: string;
  path: string;
  expandable: boolean;
  level: number;
}

@Component({
  selector: 'app-side-nav-tree',
  templateUrl: './side-nav-tree.component.html',
  styleUrls: ['./side-nav-tree.component.scss']
})
export class SideNavTreeComponent implements OnInit, OnDestroy {

  private editModeSub: Subscription;
  private matTreeSub: Subscription;
  private treeLanguageSub: Subscription;
  private providerSub: Subscription;

  private provider: string = null;
  private isBizzDev = false;

  private directoryModel: DirectoryModel;
  isInEditMode: boolean;
  isRenaming = false;
  createForm: FormGroup;
  selectedForRename: DirectoryModel;
  newName = '';
  isCreating: boolean;
  isCreatingADir: boolean;
  selectedLanguage: string;
  selectedNode: DirectoryModel;
  prohibitedChars = ['\\', '/', ':', '*', '?', '"', '<', '>', '|'];

  constructor(
    private editorService: EditorService,
    private fileService: FileService,
    private fb: FormBuilder,
    private router: Router,
    private fileOrDirService: FileOrDirService,
    private dialog: MatDialog,
    private utilityService: UtilitiyService,
    private uiService: UiService,
    private authService: AuthService) {
    }

  ngOnInit() {
    this.initTree();
  }


  // tslint:disable-next-line: variable-name
  private _transformer = (node: DirectoryModel, level: number) => {
    return {
      expandable: !node.path.endsWith('.html'),
      name: node.name,
      path: node.path,
      level
    };
  }

  // tslint:disable-next-line: member-ordering
  treeControl = new FlatTreeControl<FlatNode>(node => node.level, node => node.expandable);

  // tslint:disable-next-line: member-ordering
  treeFlattener = new MatTreeFlattener(this._transformer, node => node.level, node => node.expandable, node => node.filesOrDirectories);

  // tslint:disable-next-line: member-ordering
  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: FlatNode) => node.expandable;

  getFilePath(node: DirectoryModel): string {
    const path = node.path.substring(node.path.lastIndexOf('\\') + 1);
    const encryptedPath = path === '' ? this.utilityService.encryptString('\\') : this.utilityService.encryptString(path);
    return `/wiki/file/${encryptedPath}`;
  }

  changeNewFileNameVisibility(visible: boolean, isADir: boolean, selectedNode: DirectoryModel) {
    this.isCreating = visible;
    this.isCreatingADir = isADir;
    this.selectedNode = selectedNode;
  }

  createPageOrDir(): void {
    let isOk = true;
    this.prohibitedChars.forEach(char => {
      if (this.newName.includes(char)) {
        isOk = false;
      }
    });
    if (isOk && (!this.newName.startsWith('--')) && this.newName !== '') {
      const newAddFileOrDir = new CreateDirOrFile(this.selectedNode.path, this.newName, this.isCreatingADir);
      this.fileOrDirService.createFileOrDir(newAddFileOrDir, this.provider).subscribe(response => {
        this.fileOrDirService.emitEvent(response);
        this.isCreating = false;
        this.fileOrDirService.changeMatTree(response);
        this.uiService.showSnackbar('wiki.snackBar.create.summary', true);
        this.newName = '';
      });
    } else {
      this.uiService.showSnackbar('wiki.snackBar.wrongName.summary', true);
    }
  }

  deleteFileOrFolder(node: DirectoryModel, isADir: boolean): void {
    const fileToDelete = new FileToDelete(node.path, isADir);
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: 'Do you confirm the deletion of this data ?'
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.fileService.deleteFileOrFolder(fileToDelete, this.provider).subscribe(data => {
          this.fileOrDirService.changeMatTree(data);
          this.uiService.showSnackbar('wiki.snackBar.delete.summary', true);
        });
      }
    });
  }

  copy(toCopy: DirectoryModel): void {
    const path = toCopy.path.substring(toCopy.path.lastIndexOf('\\') + 1);
    const cryptedPath = this.utilityService.encryptString(path);
    const box = document.createElement('textarea');
    box.style.left = '0';
    box.style.top = '0';
    box.style.opacity = '0';
    box.value = `/wiki/file/${cryptedPath}`;
    document.body.appendChild(box);
    box.focus();
    box.select();
    document.execCommand('copy');
    document.body.removeChild(box);
    this.uiService.showSnackbar('wiki.snackBar.copy.summary', true);
  }

  stripHtml(html: string) {
    return this.utilityService.stripHtml(html);
  }

  private getParentNodes(fullPath: string): void {
    if (fullPath !== '' && fullPath !== '\\') {
      const removed = fullPath.substring(0, fullPath.lastIndexOf('\\'));
      const node = this.treeControl.dataNodes.find(nodes =>  nodes.path.startsWith(removed));
      this.treeControl.expand(node);
      this.getParentNodes(removed);
    }
  }

  private initTree(): void {
    const role = this.authService.getDecodedToken().role;
    this.isBizzDev = role === LicenceRoleType.bzd || role === LicenceRoleType.bko;

    this.providerSub = (this.fileService.getProviderSub().subscribe(provider => {
      this.provider = provider;
      this.fileService.getDirectoriesAndFiles(provider).subscribe(data => {
        const newRoot = data.filesOrDirectories[0];
        this.setDataSource(newRoot);
        this.fileService.changeFirstPageSub(newRoot.path);
      });
    }));

    this.createForm = this.fb.group({
      createInput: ['']
    });
    this.createForm.controls.createInput.valueChanges.subscribe(newTitle => {
      this.newName = newTitle;
    });

    this.editModeSub = this.editorService.getEditMode().subscribe(mode => {
      if (this.isBizzDev) {
        this.isInEditMode = mode;
      }
    });
    this.matTreeSub = this.fileOrDirService.getMatTree().subscribe(data => {
      this.setDataSource(data.filesOrDirectories[0]);
    });
    this.treeLanguageSub = this.fileService.getLanguageTreeSub().subscribe(() => {
      this.fileService.getDirectoriesAndFiles(this.provider).subscribe(data => {
        this.setDataSource(data.filesOrDirectories[0]);
        this.fileService.changeFirstPageSub(data.filesOrDirectories[0].path);
      });
    });
  }

  private openSideNavTree(): void {
    const regex = /%3D/gi;
    const correctedRoute = this.router.url.replace(regex, '=');
    this.treeControl.collapseAll();
    this.treeControl.expand(this.treeControl.dataNodes[0]);
    if (correctedRoute.startsWith('/wiki/file/')) {
      const fileName = this.utilityService.decryptString(correctedRoute.substring(correctedRoute.lastIndexOf('/') + 1));
      this.treeControl.dataNodes.forEach(node => {
         if (node.path && node.path.endsWith(`\\${fileName}`)) {
          this.treeControl.expand(node);
          this.getParentNodes(node.path);
        }
      });
    }
  }

  private setDataSource(data: DirectoryModel): void {
    this.directoryModel = data;
    this.dataSource.data = [this.directoryModel];
    this.openSideNavTree();
  }

  ngOnDestroy(): void {
    if (this.editModeSub) {
      this.editModeSub.unsubscribe();
    }
    if (this.matTreeSub) {
      this.matTreeSub.unsubscribe();
    }
    if (this.treeLanguageSub) {
      this.treeLanguageSub.unsubscribe();
    }
    if (this.providerSub) {
      this.providerSub.unsubscribe();
    }
  }

}
